import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ArrowUpDown, Wrench, Clock, TrendingUp } from 'lucide-react';
import { toast } from 'react-toastify';
import { CardStats, ConfirmModal } from '@components/ui';
import MaterialPageLayout from '../../components/MaterialPageLayout';
import MaterialDetailModal from '../../components/MaterialDetailModal';
import { formatCurrency } from '../../components/StockIndicator';
import AsansorTable from './AsansorTable';
import AsansorModal from './AsansorModal';
import { getAsansorByInsaat, getAsansorStats } from '@mocks/materials/asansor';
import OdemePlanMiniModal from '@components/shared/OdemePlanMiniModal';
import { addMockCariHareket } from '@mocks/cariHesaplar';

export default function Asansor() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isOdemePlanOpen, setIsOdemePlanOpen] = useState(false);
    const [odemePlanData, setOdemePlanData] = useState(null);

    const insaatId = showAllConstructions ? null : selectedConstruction?.id;
    const filteredData = useMemo(() => getAsansorByInsaat(insaatId), [insaatId]);
    const stats = useMemo(() => getAsansorStats(insaatId), [insaatId]);

    const handleAdd = () => { setSelectedItem(null); setIsModalOpen(true); };
    const handleView = (item) => { setSelectedItem(item); setIsDetailOpen(true); };
    const handleEdit = (item) => { setSelectedItem(item); setIsModalOpen(true); };
    const handleDelete = (item) => { setSelectedItem(item); setIsDeleteOpen(true); };

    const handleSubmit = async (formData, isEdit) => {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 800));
        if (!isEdit && formData.supplierId && formData.kdvDahilToplam > 0) {
            addMockCariHareket(formData.supplierId, insaatId || 'c1', {
                type: 'purchase', subtype: 'fatura', amount: formData.kdvDahilToplam,
                date: formData.siparisTarihi || new Date().toISOString().split('T')[0],
                description: `Asansör — ${formData.model} ${formData.kapasite}`,
            });
        }
        toast.success(isEdit ? 'Asansör kaydı güncellendi' : 'Yeni asansör kaydı oluşturuldu');
        setIsLoading(false); setIsModalOpen(false); setSelectedItem(null);
        if (!isEdit && formData.supplierId && formData.toplamFiyat > 0) {
            setOdemePlanData({ amount: formData.toplamFiyat, companyName: formData.tedarikci });
            setIsOdemePlanOpen(true);
        }
    };

    const handleConfirmDelete = async () => {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 500));
        toast.success('Asansör kaydı silindi');
        setIsLoading(false); setIsDeleteOpen(false); setSelectedItem(null);
    };

    const handleOdemePlanConfirm = ({ nextDueDate }) => { toast.success(`Ödeme planı oluşturuldu — Vade: ${nextDueDate}`); setIsOdemePlanOpen(false); setOdemePlanData(null); };

    const statsCards = [
        { title: 'Toplam Asansör', value: `${stats.toplamAdet} adet`, icon: ArrowUpDown, color: 'primary' },
        { title: 'Montajda', value: `${stats.montajda} adet`, icon: Wrench, color: 'warning' },
        { title: 'Bekleyen', value: `${stats.bekleyen} adet`, icon: Clock, color: 'info' },
        { title: 'Toplam Maliyet', value: formatCurrency(stats.toplamMaliyet), icon: TrendingUp, color: 'success' },
    ];

    const detailFields = [
        { key: 'siparisTarihi', label: 'Sipariş Tarihi', type: 'date' },
        { key: 'teslimTarihi', label: 'Tahmini Teslim', type: 'date' },
        { key: 'tedarikci', label: 'Tedarikçi / Marka' },
        { key: 'model', label: 'Model' },
        { key: 'kapasite', label: 'Kapasite' },
        { key: 'kat', label: 'Kat Sayısı', type: 'number' },
        { key: 'durakSayisi', label: 'Durak Sayısı', type: 'number' },
        { key: 'toplamFiyat', label: 'Toplam Tutar', type: 'currency' },
        { key: 'durum', label: 'Durum', type: 'status' },
        { key: 'montajIlerleme', label: 'Montaj İlerleme (%)', type: 'number' },
        { key: 'blokAdi', label: 'Blok' },
        { key: 'garanti', label: 'Garanti Süresi' },
        { key: 'bakimSozlesmesi', label: 'Bakım Sözleşmesi', type: 'boolean' },
    ];

    return (
        <>
            <MaterialPageLayout title="Asansör" subtitle="Tesisat & Mekanik" icon={ArrowUpDown}
                iconBgColor="bg-violet-600" backLink="/insaat/tesisat" stats={statsCards}
                onAddClick={handleAdd}>
                <AsansorTable data={filteredData} onView={handleView} onEdit={handleEdit} onDelete={handleDelete} />
            </MaterialPageLayout>

            <AsansorModal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setSelectedItem(null); }}
                onSubmit={handleSubmit} initialData={selectedItem} isLoading={isLoading} insaatId={insaatId} />
            <MaterialDetailModal isOpen={isDetailOpen} onClose={() => { setIsDetailOpen(false); setSelectedItem(null); }}
                data={selectedItem} fields={detailFields} title="Asansör Detayı" />
            <ConfirmModal isOpen={isDeleteOpen} onClose={() => { setIsDeleteOpen(false); setSelectedItem(null); }}
                onConfirm={handleConfirmDelete} title="Kaydı Sil"
                message={`"${selectedItem?.blokAdi}" için ${selectedItem?.marka} ${selectedItem?.model} asansör kaydını silmek istediğinize emin misiniz?`}
                confirmText="Evet, Sil" isLoading={isLoading} />
            <OdemePlanMiniModal isOpen={isOdemePlanOpen} onClose={() => { setIsOdemePlanOpen(false); setOdemePlanData(null); }} onConfirm={handleOdemePlanConfirm} amount={odemePlanData?.amount} companyName={odemePlanData?.companyName} />
        </>
    );
}
